import React, { useState } from 'react';
import { Link, Copy, Check, Code, MailCheck } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения результатов генерации
 */
const ResultDisplay = ({ 
  mailtoLink, 
  htmlCode, 
  markdownCode, 
  copyToClipboard,
  showCopiedMessage,
  copyType
}) => {
  const [activeTab, setActiveTab] = useState('link');
  
  if (!mailtoLink) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-800 shadow-md p-5 mb-8">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <MailCheck className="w-6 h-6 mr-2 text-green-500" />
        Ваша Mailto-ссылка готова
      </h3>

      {/* Вкладки для разных форматов */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('link')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${
            activeTab === 'link'
              ? 'text-green-600 dark:text-green-400 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Ссылка
        </button>
        <button
          onClick={() => setActiveTab('html')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${
            activeTab === 'html'
              ? 'text-green-600 dark:text-green-400 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          HTML
        </button>
        <button
          onClick={() => setActiveTab('markdown')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${
            activeTab === 'markdown'
              ? 'text-green-600 dark:text-green-400 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Markdown
        </button>
      </div>

      {/* Контент вкладок */}
      <div className="mb-4">
        {activeTab === 'link' && (
          <div>
            <div className="relative">
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 text-sm font-mono break-all">
                {mailtoLink}
              </div>
              <div className="absolute right-2 top-2">
                <Button
                  onClick={() => copyToClipboard(mailtoLink, 'link')}
                  variant="ghost"
                  size="sm"
                  title="Копировать ссылку"
                  icon={showCopiedMessage && copyType === 'link' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Используйте эту ссылку в качестве URL для вашей кнопки или ссылки "Написать письмо".
            </p>
          </div>
        )}

        {activeTab === 'html' && (
          <div>
            <div className="relative">
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 text-sm font-mono break-all">
                {htmlCode}
              </div>
              <div className="absolute right-2 top-2">
                <Button
                  onClick={() => copyToClipboard(htmlCode, 'html')}
                  variant="ghost"
                  size="sm"
                  title="Копировать HTML-код"
                  icon={showCopiedMessage && copyType === 'html' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              HTML-код ссылки для вставки на ваш веб-сайт или в HTML-письмо.
            </p>
          </div>
        )}

        {activeTab === 'markdown' && (
          <div>
            <div className="relative">
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 text-sm font-mono break-all">
                {markdownCode}
              </div>
              <div className="absolute right-2 top-2">
                <Button
                  onClick={() => copyToClipboard(markdownCode, 'markdown')}
                  variant="ghost"
                  size="sm"
                  title="Копировать Markdown"
                  icon={showCopiedMessage && copyType === 'markdown' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Markdown-код для вставки в документы, поддерживающие Markdown (GitHub, Notion, и т.д.).
            </p>
          </div>
        )}
      </div>

      {/* Уведомление о копировании */}
      {showCopiedMessage && (
        <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-2 rounded-lg text-sm flex items-center">
          <Check className="w-4 h-4 mr-2" />
          Скопировано в буфер обмена!
        </div>
      )}

      {/* Подсказки по использованию */}
      <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
        <div className="flex items-start">
          <Code className="w-5 h-5 mr-3 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">Как использовать Mailto-ссылку?</p>
            <p>Вы можете добавить эту ссылку на вашу веб-страницу, в электронное письмо или подпись. 
            При клике пользователь перейдет в свой почтовый клиент с уже заполненными полями письма.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultDisplay;